import { useEffect, useMemo, useState, type HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  CircularProgress,
  Chip,
  DropdownField,
  type DropdownOption,
} from '@gapartners/ui';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { closeWarmTransferModal } from '@/store/slices/warmTransferSlice';
import {
  useTransferTargetOptions,
  type TransferTargetOption,
} from '@/hooks/useTransferTargetOptions';
import { useWarmTransfer } from '@/hooks/useWarmTransfer';
import type { ConnectContact } from '@/types';
import { isChatContact } from '@/components/amazon-connect/contactUtils';
import { useElapsed } from '@/components/amazon-connect/useElapsed';

type Props = {
  activeContact: ConnectContact | undefined;
  hasActiveCall: boolean;
};

type WarmTransferTargetDropdownOption = TransferTargetOption & {
  id: string;
  primary: string;
  secondary?: string;
  searchText: string;
};

type WarmQueueDropdownOption = {
  id: string;
  label: string;
  primary: string;
  searchText: string;
  secondary?: string;
  online?: boolean;
};

function splitOptionLabel(label: string): {
  primary: string;
  secondary?: string;
} {
  const [primary, ...rest] = label.split('\n');
  const secondary = rest.join('\n').trim();
  return { primary: primary.trim(), secondary: secondary || undefined };
}

function renderSearchableDropdownOption(
  props: HTMLAttributes<HTMLLIElement>,
  option: { id: string; primary: string; secondary?: string; online?: boolean }
) {
  return (
    <li {...props} key={option.id}>
      <Box
        sx={{
          py: 0.75,
          width: '100%',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {typeof option.online === 'boolean' && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: option.online ? 'success.main' : 'text.disabled',
                flexShrink: 0,
              }}
            />
          )}
          <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
            {option.primary}
          </Typography>
        </Box>
        {option.secondary ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.3, mt: 0.25 }}
          >
            {option.secondary}
          </Typography>
        ) : null}
      </Box>
    </li>
  );
}

const searchableDropdownSx = {
  '& .MuiAutocomplete-inputRoot': {
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  '& .MuiAutocomplete-input': {
    whiteSpace: 'normal',
    overflow: 'hidden',
    textOverflow: 'clip',
  },
};

export function WarmTransferModal({ activeContact, hasActiveCall }: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    isModalOpen,
    modalView,
    stage,
    targetDisplayName,
    targetType,
    answeringDisplayName,
    consultAgentJoined,
  } = useAppSelector(state => state.warmTransfer);
  const call = useAppSelector(state => state.call);
  const customerOnHold = call.customerOnHold;
  const customerOnHoldSince = call.customerOnHoldSince;
  const holdElapsed = useElapsed(customerOnHoldSince);
  const caseInfo = useAppSelector(state => state.case.currentCase);

  const {
    agentTargetOptions,
    queueOptions,
    loadingTargets,
    loadingQueues,
    refreshTargets,
    refreshQueues,
  } = useTransferTargetOptions(activeContact);

  // Warm transfer requires a live consult target. Exclude offline/DID-only
  // agents so the agent never picks a target that cannot accept the request.
  const warmAgentTargetOptions = useMemo(
    () =>
      agentTargetOptions.filter(
        o => o.online === true && o.type !== 'teams_group'
      ),
    [agentTargetOptions]
  );
  const warmGroupTargetOptions = useMemo(
    () => agentTargetOptions.filter(o => o.type === 'teams_group'),
    [agentTargetOptions]
  );

  useEffect(() => {
    if (!isModalOpen || modalView !== 'selectTarget') return;
    refreshTargets();
    refreshQueues();
  }, [isModalOpen, modalView, refreshTargets, refreshQueues]);

  const {
    startWarmTransferFlow,
    cancelWarmTransfer,
    conferenceWarmTransfer,
    completeWarmTransfer,
    isWarmInProgress,
  } = useWarmTransfer(activeContact);

  const [targetKind, setTargetKind] = useState<'agent' | 'queue'>('agent');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [selectedQueueValue, setSelectedQueueValue] = useState('');

  const resetFormState = () => {
    setTargetKind('agent');
    setSelectedAgentId('');
    setSelectedQueueValue('');
  };

  useEffect(() => {
    if (!isModalOpen) {
      resetFormState();
    }
  }, [isModalOpen]);

  const selectedAgent = warmAgentTargetOptions.find(
    o => o.value === selectedAgentId
  );
  const isTeamsTargetSelected =
    targetKind === 'agent' &&
    ['teams_user', 'teams_group'].includes(selectedAgent?.type ?? '');

  const agentDropdownOptions = useMemo<WarmTransferTargetDropdownOption[]>(
    () =>
      warmAgentTargetOptions.map(option => {
        const { primary, secondary } = splitOptionLabel(option.label);
        return {
          ...option,
          id: option.value,
          primary,
          secondary,
          searchText: option.label.replace(/\n/g, ' ').toLowerCase(),
        };
      }),
    [warmAgentTargetOptions]
  );

  const selectedAgentOption = useMemo(
    () =>
      agentDropdownOptions.find(option => option.id === selectedAgentId) ??
      null,
    [agentDropdownOptions, selectedAgentId]
  );

  const handleAgentChange = (
    _event: unknown,
    value: DropdownOption | DropdownOption[] | null
  ) => {
    const selected = Array.isArray(value) ? value[0] : value;
    setSelectedAgentId(selected ? String(selected.id) : '');
  };

  const queueDropdownOptions = useMemo<WarmQueueDropdownOption[]>(() => {
    const queues: WarmQueueDropdownOption[] = queueOptions.map(option => ({
      id: option.value,
      label: option.label,
      primary: option.label,
      searchText: option.label.toLowerCase(),
    }));
    const groups: WarmQueueDropdownOption[] = warmGroupTargetOptions.map(
      option => {
        const { primary, secondary } = splitOptionLabel(option.label);
        return {
          id: option.value,
          label: option.label,
          primary,
          secondary,
          searchText: option.label.replace(/\n/g, ' ').toLowerCase(),
          online: option.online,
        };
      }
    );
    return [...queues, ...groups];
  }, [queueOptions, warmGroupTargetOptions]);

  const selectedQueueOption = useMemo(
    () =>
      queueDropdownOptions.find(option => option.id === selectedQueueValue) ??
      null,
    [queueDropdownOptions, selectedQueueValue]
  );

  const handleQueueChange = (
    _event: unknown,
    value: DropdownOption | DropdownOption[] | null
  ) => {
    const selected = Array.isArray(value) ? value[0] : value;
    setSelectedQueueValue(selected ? String(selected.id) : '');
  };

  const contextPreview = useMemo(
    () => ({
      customer: call.callerName ?? '—',
      phone: call.callerPhoneNumber ?? '—',
      caseLine: caseInfo
        ? `#${caseInfo.id} · ${caseInfo.subject}`
        : t('components.contactCenter.warmTransferNoCase', 'No linked case'),
    }),
    [call.callerName, call.callerPhoneNumber, caseInfo, t]
  );

  const handleClosePicker = () => {
    if (isWarmInProgress) return;
    resetFormState();
    dispatch(closeWarmTransferModal());
  };

  const handleStart = () => {
    if (targetKind === 'queue') {
      if (!selectedQueueValue) return;
      const group = warmGroupTargetOptions.find(
        o => o.value === selectedQueueValue
      );
      if (group) {
        void startWarmTransferFlow({ kind: 'teams_group', option: group });
        return;
      }
      const queue = queueOptions.find(o => o.value === selectedQueueValue);
      if (queue) {
        void startWarmTransferFlow({ kind: 'queue', option: queue });
      }
      return;
    }
    if (selectedAgent) {
      void startWarmTransferFlow({
        kind: selectedAgent.type,
        option: selectedAgent,
      });
    }
  };

  const statusLabel = useMemo(() => {
    if (stage === 'waitingForAccept') {
      return t('components.contactCenter.warmTransferWaitingAccept', {
        name: targetDisplayName ?? '',
        defaultValue: 'Waiting for {{name}} to accept…',
      });
    }
    if (stage === 'dialing') {
      if (targetType === 'queue' || !answeringDisplayName) {
        return t('components.contactCenter.warmTransferCallingQueue', {
          name: targetDisplayName ?? '',
          defaultValue: 'Calling queue: {{name}}…',
        });
      }
      return t('components.contactCenter.warmTransferCalling', {
        name: targetDisplayName ?? '',
        defaultValue: 'Calling {{name}}…',
      });
    }
    if (stage === 'consulting') {
      return t('components.contactCenter.warmTransferConsulting', {
        name: targetDisplayName ?? '',
      });
    }
    if (stage === 'conference') {
      return t(
        'components.contactCenter.warmTransferConference',
        '3-way call — you, consult agent, and customer'
      );
    }
    return '';
  }, [stage, targetDisplayName, targetType, answeringDisplayName, t]);

  const voiceBlocked = activeContact && isChatContact(activeContact);

  return (
    <Dialog
      open={isModalOpen}
      onClose={handleClosePicker}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isWarmInProgress}
    >
      <DialogTitle>
        {t('components.contactCenter.warmTransferTitle', 'Warm transfer')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2">
              {contextPreview.customer} · {contextPreview.phone}
            </Typography>
            {modalView === 'inProgress' && customerOnHold && (
              <Chip
                size="small"
                label={
                  customerOnHoldSince != null
                    ? t(
                        'components.contactCenter.warmTransferCustomerOnHoldTimer',
                        {
                          time: holdElapsed,
                          defaultValue: 'Customer on hold · {{time}}',
                        }
                      )
                    : t(
                        'components.contactCenter.warmTransferCustomerOnHold',
                        'Customer on hold'
                      )
                }
                color="warning"
              />
            )}
          </Box>

          {modalView === 'selectTarget' && (
            <>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant={targetKind === 'agent' ? 'contained' : 'outlined'}
                  onClick={() => setTargetKind('agent')}
                >
                  {t(
                    'components.contactCenter.warmTransferTargetAgent',
                    'Agent / Teams'
                  )}
                </Button>
                <Button
                  size="small"
                  variant={targetKind === 'queue' ? 'contained' : 'outlined'}
                  onClick={() => setTargetKind('queue')}
                >
                  {t(
                    'components.contactCenter.warmTransferTargetQueue',
                    'Queue'
                  )}
                </Button>
              </Box>

              {targetKind === 'agent' ? (
                <Box>
                  <DropdownField
                    label={t('components.contactCenter.selectTransferTarget')}
                    labelString={t(
                      'components.contactCenter.selectTransferTarget'
                    )}
                    multiple={false}
                    options={agentDropdownOptions}
                    value={selectedAgentOption}
                    onChange={handleAgentChange}
                    onOpen={refreshTargets}
                    loading={loadingTargets}
                    disabled={
                      loadingTargets && agentDropdownOptions.length === 0
                    }
                    getOptionId={option => option.id}
                    getOptionLabel={option =>
                      (option as WarmTransferTargetDropdownOption).displayName
                    }
                    filterOption={(option, inputValue) =>
                      (
                        option as WarmTransferTargetDropdownOption
                      ).searchText.includes(inputValue.toLowerCase())
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    sx={searchableDropdownSx}
                    textFieldProps={{
                      placeholder: t(
                        'components.contactCenter.searchTransferTarget'
                      ),
                    }}
                    renderOption={(props, option) =>
                      renderSearchableDropdownOption(
                        props,
                        option as WarmTransferTargetDropdownOption
                      )
                    }
                    fullWidth
                  />
                  {loadingTargets && agentDropdownOptions.length === 0 ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.75, display: 'block' }}
                    >
                      {t('components.contactCenter.loadingTransferTargets')}
                    </Typography>
                  ) : null}
                </Box>
              ) : (
                <Box>
                  <DropdownField
                    label={t('components.amazonConnect.selectQueue')}
                    labelString={t('components.amazonConnect.selectQueue')}
                    multiple={false}
                    options={queueDropdownOptions}
                    value={selectedQueueOption}
                    onChange={handleQueueChange}
                    onOpen={refreshQueues}
                    loading={loadingQueues}
                    disabled={
                      loadingQueues && queueDropdownOptions.length === 0
                    }
                    getOptionId={option => option.id}
                    getOptionLabel={option => option.label}
                    filterOption={(option, inputValue) =>
                      (option as WarmQueueDropdownOption).searchText.includes(
                        inputValue.toLowerCase()
                      )
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    sx={searchableDropdownSx}
                    textFieldProps={{
                      placeholder: t('components.amazonConnect.searchQueue'),
                    }}
                    renderOption={(props, option) =>
                      renderSearchableDropdownOption(
                        props,
                        option as WarmQueueDropdownOption
                      )
                    }
                    fullWidth
                  />
                  {loadingQueues && queueDropdownOptions.length === 0 ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.75, display: 'block' }}
                    >
                      {t(
                        'components.contactCenter.loadingQueueTargets',
                        'Loading queue targets...'
                      )}
                    </Typography>
                  ) : null}
                </Box>
              )}

              {isTeamsTargetSelected ? (
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      'components.contactCenter.warmTransferTeamsNoContext',
                      'Context and notes are not shared when transferring to Teams users.'
                    )}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      {t(
                        'components.contactCenter.warmTransferContextPreview',
                        'Context for receiving agent'
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {contextPreview.caseLine}
                    </Typography>
                  </Box>
                </>
              )}
            </>
          )}

          {modalView === 'inProgress' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                alignItems: 'center',
                py: 2,
              }}
            >
              {(stage === 'holdingCustomer' ||
                stage === 'dialing' ||
                stage === 'waitingForAccept') && <CircularProgress size={32} />}
              <Typography variant="body1" textAlign="center">
                {statusLabel}
              </Typography>
              {targetDisplayName && (
                <Chip
                  label={targetDisplayName}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          )}

          {!hasActiveCall && (
            <Typography variant="body2" color="text.secondary">
              {t('components.contactCenter.transferAvailableDuringCall')}
            </Typography>
          )}
          {voiceBlocked && (
            <Typography variant="body2" color="error">
              {t(
                'components.contactCenter.warmTransferVoiceOnly',
                'Warm transfer is only available for voice calls.'
              )}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {modalView === 'selectTarget' && (
          <>
            <Button variant="outlined" onClick={handleClosePicker}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleStart}
              disabled={
                !hasActiveCall ||
                !!voiceBlocked ||
                (targetKind === 'agent'
                  ? !selectedAgentId
                  : !selectedQueueValue)
              }
            >
              {t(
                'components.contactCenter.warmTransferStart',
                'Start warm transfer'
              )}
            </Button>
          </>
        )}
        {modalView === 'inProgress' && (
          <>
            <Button
              variant="outlined"
              color="inherit"
              onClick={cancelWarmTransfer}
            >
              {t(
                'components.contactCenter.warmTransferCancel',
                'Cancel transfer'
              )}
            </Button>
            {(stage === 'consulting' || stage === 'conference') &&
              (targetType === 'teams_user' ||
                targetType === 'teams_group' ||
                consultAgentJoined) && (
                <>
                  {stage === 'consulting' && (
                    <Button variant="outlined" onClick={conferenceWarmTransfer}>
                      {t(
                        'components.contactCenter.warmTransferConferenceBtn',
                        'Conference'
                      )}
                    </Button>
                  )}
                  <Button variant="contained" onClick={completeWarmTransfer}>
                    {t(
                      'components.contactCenter.warmTransferComplete',
                      'Complete transfer'
                    )}
                  </Button>
                </>
              )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
